import {Card, Spin} from "antd";
import React, {useContext, useEffect, useRef, useState} from "react";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {
    Organization,
    PaymentStatus,
    MonetaryType,
    SubscriptionResponse
} from "../../api-client";
import {FormattedMessage, useIntl} from "react-intl";
import {isMobileOnly} from "react-device-detect";
import DoctorImg from "../../assets/doctor.svg";
import moment from "moment";
import {AlertContext} from "../../context/AlertContext";
import CustomAlert from "../layout/CustomAlert";
import {getOrganizationApi} from "../../api/apiInstances";
import {SubscriptionState} from "../../constants/subscriptionStates";
import logger from "../../utils/logger";


const DashboardWelcomeCard = () => {

    const daysOfWeek = [
        <FormattedMessage key="sunday" id="sunday"/>,
        <FormattedMessage key="monday" id="monday"/>,
        <FormattedMessage key="tuesday" id="tuesday"/>,
        <FormattedMessage key="wednesday" id="wednesday"/>,
        <FormattedMessage key="thursday" id="thursday"/>,
        <FormattedMessage key="friday" id="friday"/>,
        <FormattedMessage key="saturday" id="saturday"/>,
    ]

    const organizationApi = getOrganizationApi();

    const [subscription, setSubscription] = useState<SubscriptionResponse | undefined>();
    const [organization, setOrganization] = useState<Organization | undefined>(undefined);
    const [dismissedForToday, setDismissedForToday] = useState(false);
    const [loading, setLoading] = useState(true);
    const {principal, setSelectSubscription} = React.useContext(DentobookApplicationContext);
    const {setVisible, setError, setWarning} = useContext(AlertContext);
    const intl = useIntl();


    const handleDismiss = () => {
        const today = moment().format("YYYY-MM-DD");
        localStorage.setItem("dismissedReminderDate", today);
        setDismissedForToday(true);
        setVisible(false);
    };

    useEffect(() => {
        organizationApi.fetchOrganizationById(principal?.user?.organizationId as string)
            .then((response) => {
                // @ts-ignore
                setOrganization(response.data);
                setSubscription(response.data.subscription ?? undefined);
                setLoading(false);
            })
            .catch(error => {
                logger.error("Failed to load organization:", error);
                logger.log(error);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        let isActive = true;
        const checkDismissStatus = () => {
            const dismissedDate = localStorage.getItem("dismissedReminderDate");
            const today = moment().format("YYYY-MM-DD");
            setDismissedForToday(dismissedDate === today);
        };

        checkDismissStatus();

        if (organization && organization.paymentStatus === PaymentStatus.Failed && isActive) {
            // Use plain text for payment failure message
            setWarning(intl.formatMessage({id: "subscription_payment_failed"}));
            setWarning(<div style={{cursor: 'pointer'}} onClick={() => setSelectSubscription(SubscriptionState.SHOW_PAID_PLAN_SELECTED)}>
                <FormattedMessage id="subscription_payment_failed"/>
            </div>);
            setVisible(true);
        } else if (
            subscription?.plan?.monetaryType === MonetaryType.FreeTrial &&
            subscription.expiration &&
            !dismissedForToday
        ) {
            const remainingDays = moment(subscription.expiration).diff(moment(), "days");

            if (remainingDays <= 10 && remainingDays > 0 && isActive) {
                const unit =
                    remainingDays === 1
                        ? intl.locale === "BG" ? "ден" : "day"
                        : intl.locale === "BG" ? "дни" : "days";
                const linkStyle = {
                    color: "#2e363e",
                    textDecoration: "underline",
                    fontWeight: "bold",
                    cursor: "pointer",
                };

                setWarning(
                    <>
                        <FormattedMessage
                            id="subscription_active_warning"
                            values={{
                                days: remainingDays,
                                unit: unit,
                                a: (
                                    <a
                                        href="#"
                                        style={{color: "#2e363e", textDecoration: "underline", fontWeight: "bold"}}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setSelectSubscription(SubscriptionState.SHOW_PLAN_SELECTION);
                                        }}
                                    >
                                        {intl.locale === "BG" ? "натиснете тук" : "click here"}
                                    </a>
                                ),
                            }}
                        />
                    </>
                );

                setVisible(true);
            } else if (remainingDays <= 0 && isActive) {
                const linkText = intl.locale === "BG" ? "натиснете тук" : "click here";

                const linkStyle = {
                    color: "#2e363e",
                    textDecoration: "underline",
                    fontWeight: "bold",
                    cursor: "pointer",
                };
                setError(
                    <FormattedMessage
                        id="subscription_expired_warning"
                        values={{
                            a: (
                                <a
                                    href="#"
                                    style={linkStyle}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setSelectSubscription(SubscriptionState.SHOW_PLAN_SELECTION);
                                    }}
                                >
                                    {linkText}
                                </a>
                            ),
                        }}
                    />
                );
                setVisible(true);
            }
        }
        return () => {
            isActive = false; // Mark the component as inactive
            setWarning('');
            setError('');
            setVisible(false);
        };
    }, [
        subscription,
        intl,
        setWarning,
        setError,
        setVisible,
        dismissedForToday,
        setSelectSubscription,
        organization?.paymentStatus
    ]);


    return (
        <>
            <CustomAlert
                customOnClose={
                    subscription?.plan?.monetaryType === MonetaryType.FreeTrial
                        ? handleDismiss
                        : undefined
                }
            />
            {loading ? (
                <Card
                    bordered={true}
                    style={{height: isMobileOnly ? 278 : 270}}
                    className="dentobook-dashboard-card dentobook-dashboard-welcome-card"
                >
                    <div style={{ textAlign: 'center', padding: '80px 0' }}>
                        <Spin tip={intl.formatMessage({ id: 'loading' })} />
                    </div>
                </Card>
            ) : isMobileOnly ? (
                <Card
                    bordered={true}
                    styles={{body: {padding: "0"}}}
                    style={{height: 278}}
                    className="dentobook-dashboard-card dentobook-dashboard-welcome-card"
                >
                    <div>
                        <h3>
                            <FormattedMessage id="welcome"/>,{" "}
                            <strong>
                                {organization?.name?.substring(0, 40) +
                                    (organization?.name?.length! > 40 ? "..." : "")}
                            </strong>
                        </h3>
                    </div>
                    <div>
                        <div style={{float: "left", width: "49%"}}>
                            <h2 className={"dentobook-dashboard-time"}>
                                {moment(new Date()).format("HH:mm")}
                            </h2>
                            <h3>
                                {daysOfWeek[new Date().getDay()]},{" "}
                                {moment(new Date()).format("DD.MM.YYYY")}
                            </h3>
                        </div>
                        <div
                            style={{
                                float: "right",
                                width: "49%",
                                height: "100%",
                                marginTop: 20,
                            }}
                        >
                            <img alt="doctor" src={DoctorImg} height={120}/>
                        </div>
                    </div>
                </Card>
            ) : (
                <Card
                    bordered={true}
                    style={{height: 270}}
                    styles={{body: {padding: "0"}}}
                    className="dentobook-dashboard-card dentobook-dashboard-welcome-card"
                >
                    <div style={{float: "left"}}>
                        <span className="font-34">
                            <FormattedMessage id="welcome"/>,{" "}
                            <strong>
                                {organization?.name?.substring(0, 30) +
                                    (organization?.name?.length! > 30 ? "..." : "!")}
                            </strong>
                        </span>
                        <div style={{position: "absolute", bottom: 10}}>
                            <h1 className={"dentobook-dashboard-time"}>
                                {moment(new Date()).format("HH:mm")}
                            </h1>
                            <h3
                                style={{
                                    marginBottom: "16px",
                                    marginTop: "12px",
                                }}
                            >
                                {daysOfWeek[new Date().getDay()]},{" "}
                                {moment(new Date()).format("DD.MM.YYYY")}
                            </h3>
                        </div>
                    </div>
                    {organization?.avatarUrl && (
                        <div style={{position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)"}}>
                            <img
                                src={organization.avatarUrl}
                                alt={organization.name}
                                style={{width: 120, height: 120, borderRadius: "50%", objectFit: "cover", boxShadow: "0 2px 8px rgba(0,0,0,0.12)"}}
                            />
                        </div>
                    )}
                    <div style={{float: "right", marginTop: -20}}>
                        <img alt="doctor" src={DoctorImg}/>
                    </div>
                </Card>
            )}
        </>
    );
};

export default DashboardWelcomeCard;